import { useEffect, useRef } from 'react'
import Link from 'next/link'
import useSWRInfinite from 'swr/infinite'
import { motion } from 'framer-motion'
import { CheckCircle, ExternalLink } from 'lucide-react'
import { useTranslations } from 'next-intl'

import type { Asset } from '@/lib/types'
import { cn } from '@/lib/utils'
import { useEditorStore } from '@/hooks/use-editor-store'
import { useProjectStore } from '@/hooks/use-project-store'

type PixabayGraphic = {
  id: number
  webformatURL: string
  previewURL: string
  largeImageURL: string
  user: string
  tags: string
  imageWidth: number
  imageHeight: number
}

type PixabaySearchResponse = {
  hits: PixabayGraphic[]
  total: number
}

const PER_PAGE = 12

interface GraphicLibraryProps {
  query: string
  selectedAssets: Asset[]
  onSelect: (e: React.MouseEvent, asset: Asset) => void
  limit?: number
  isExpanded?: boolean
}

export const GraphicLibrary = ({
  query,
  selectedAssets,
  onSelect,
  limit,
  isExpanded = false,
}: GraphicLibraryProps) => {
  const t = useTranslations('graphic-library')
  const loadMoreRef = useRef<HTMLDivElement>(null)
  const { setIsToolboxOpen } = useProjectStore()
  const { getIsEditorOn, getIsTargetImage, getIsTargetVideo, setTargetSrc } =
    useEditorStore()
  const isEditorOn = getIsEditorOn()
  const isImageSelectedInEditor = getIsEditorOn() && getIsTargetImage()

  const getKey = (
    pageIndex: number,
    previousPageData: PixabaySearchResponse | null,
  ) => {
    if (limit && pageIndex > 0) return null // Only fetch first page if limit is set
    if (previousPageData && !previousPageData.hits.length) return null
    const searchParam = query ? `&q=${encodeURIComponent(query)}` : ''
    return `/api/media/pixabay?type=vector&page=${pageIndex + 1}&per_page=${limit || PER_PAGE}${searchParam}`
  }

  const { data, size, setSize, isLoading } =
    useSWRInfinite<PixabaySearchResponse>(
      getKey,
      async (url) => {
        const res = await fetch(url)
        if (!res.ok) throw new Error('Failed to fetch')
        return res.json()
      },
      {
        revalidateFirstPage: false,
      },
    )

  const allGraphics = data ? data.flatMap((page) => page.hits) : []
  const graphics = limit ? allGraphics.slice(0, limit) : allGraphics
  const isLoadingMore =
    isLoading || (size > 0 && data && typeof data[size - 1] === 'undefined')
  const isEmpty = !data?.[0] || data[0].hits.length === 0
  const isReachingEnd =
    isEmpty || (data && data[data.length - 1].hits.length < PER_PAGE)

  useEffect(() => {
    if (!loadMoreRef.current || isLoadingMore || isReachingEnd || limit) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setSize(size + 1)
        }
      },
      { rootMargin: '50px' },
    )

    observer.observe(loadMoreRef.current)
    return () => observer.disconnect()
  }, [isLoadingMore, isReachingEnd, setSize, size, limit])

  const handleGraphicSelect = (
    graphic: PixabayGraphic,
    e: React.MouseEvent,
  ) => {
    if (isEditorOn && !isImageSelectedInEditor) return

    const assetData: Asset = {
      source: 'elements',
      type: 'graphic',
      link: graphic.largeImageURL,
      thumbnail: graphic.previewURL,
    }

    if (isImageSelectedInEditor) {
      setIsToolboxOpen(false)
      setTargetSrc(graphic.largeImageURL)
    } else {
      onSelect(e, assetData)
    }
  }

  if (isLoadingMore && size === 1) {
    return (
      <div className="text-center py-4">
        <p className="text-sm text-gray-500">{t('loading')}</p>
      </div>
    )
  }

  if (isEmpty) {
    return (
      <div className="text-center py-4">
        <p className="text-sm text-gray-500">{t('no_graphics')}</p>
      </div>
    )
  }

  return (
    <>
      {graphics.map((graphic) => (
        <motion.div
          key={graphic.id}
          className={cn(
            'flex-none aspect-square bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden group relative cursor-pointer',
            isExpanded ? 'w-fit' : 'w-[80px]',
            isEditorOn && !isImageSelectedInEditor
              ? 'opacity-50 cursor-not-allowed'
              : 'cursor-pointer',
          )}
          onClick={(e) => handleGraphicSelect(graphic, e)}
        >
          <img
            src={graphic.previewURL}
            alt={graphic.tags}
            className="w-full h-full object-cover transition-transform group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-2">
            <p className="text-xs text-white line-clamp-2">
              {t('graphic_info', {
                user: graphic.user,
                width: graphic.imageWidth,
                height: graphic.imageHeight,
              })}
            </p>
          </div>
          {selectedAssets.some(
            (asset) => asset.link === graphic.largeImageURL,
          ) && (
            <div className="absolute top-2 left-2 bg-brand text-white rounded-full p-1">
              <CheckCircle className="w-4 h-4" />
            </div>
          )}
          <div className="absolute top-2 right-2 p-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <Link
              href={graphic.largeImageURL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-gray-300 hover:text-white transition-colors"
              onClick={(e) => e.stopPropagation()}
              title={t('open_in_new')}
            >
              <ExternalLink className="w-4 h-4" />
            </Link>
          </div>
        </motion.div>
      ))}
      {!limit && <div ref={loadMoreRef} className="h-4" />}
      {isLoadingMore && !limit && (
        <div className="text-center py-4">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('loading_more')}
          </p>
        </div>
      )}
    </>
  )
}
